"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { ShoppingBag, ArrowLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/stores/cart-store";
import { CartSheet } from "@/components/order/cart-sheet";
import {
  CartScopeProvider,
  cartScopeForPathname,
  checkoutPathForScope,
  useActiveCartStore,
} from "@/components/order/cart-scope";
import { useOrderingState } from "@/hooks/use-ordering-state";
import { shouldResetDeliveryOrderType } from "@/lib/ordering-state";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { useCateringPublicConfig } from "@/components/order/catering-fulfillment-panel";

export default function OrderLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  // The catering routes get their own persisted cart; everything else under
  // /order shares the meal cart. The provider is keyed on the scope so a
  // navigation between the two never reuses a store subscription.
  const pathname = usePathname();
  // A tenant without live catering ordering keeps the legacy behaviour on
  // /order/catering: trays go into the meal cart (pickup only).
  const cateringConfig = useCateringPublicConfig();
  const scope =
    cateringConfig?.enabled === true ? cartScopeForPathname(pathname) : "meal";
  return (
    <CartScopeProvider key={scope} scope={scope}>
      <OrderShell scope={scope} pathname={pathname}>
        {children}
      </OrderShell>
    </CartScopeProvider>
  );
}

function OrderShell({
  scope,
  pathname,
  children,
}: {
  scope: "meal" | "catering";
  pathname: string | null;
  children: React.ReactNode;
}) {
  const [mounted, setMounted] = useState(false);
  const useCart = useActiveCartStore();
  const itemCount = useCart((state) => state.getItemCount());
  const total = useCart((state) => state.getTotal());
  const orderType = useCart((state) => state.orderType);
  const setOrderType = useCart((state) => state.setOrderType);
  const isCatering = scope === "catering";
  // Host-aware: resolve the tenant from the request host via the marketing
  // location context so the wordmark never renders one brand (e.g. Roma) on the
  // other tenant's host on the shared deployment.
  const { siteConfig } = useMarketingLocationContext();

  const isCheckoutPage = pathname === checkoutPathForScope(scope);
  // Static capability AND runtime operator switch (fails dark while loading).
  const {
    deliveryOrderingEnabled,
    isLoading: orderingStateLoading,
  } = useOrderingState();

  // Prevent hydration mismatch - cart values come from localStorage
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // Meal cart only: the catering cart's delivery switch is the tenant's
    // catering configuration, not the Uber Direct ordering flag.
    if (isCatering) return;
    if (
      shouldResetDeliveryOrderType({
        isLoading: orderingStateLoading,
        deliveryOrderingEnabled,
        orderType,
      })
    ) {
      setOrderType("pickup");
    }
  }, [
    isCatering,
    deliveryOrderingEnabled,
    orderType,
    orderingStateLoading,
    setOrderType,
  ]);

  return (
    <div
      data-catering-checkout={isCatering && isCheckoutPage ? "true" : undefined}
      data-order-light-surface="true"
      className="flex min-h-screen flex-col bg-background text-foreground"
    >
      {/* Single order header — the one and only top bar (back + name + reservations + cart) */}
      <header className="sticky top-0 z-50 h-14 border-b border-border bg-background">
        <div className="container flex h-14 items-center justify-between gap-3 px-4 sm:px-6">
          {/* Left: Back + wordmark */}
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="h-11 w-11 shrink-0 rounded-full hover:bg-muted"
            >
              <Link href="/">
                <ArrowLeft className="h-5 w-5" />
                <span className="sr-only">Back to home</span>
              </Link>
            </Button>
            <Link
              href={isCatering ? "/order/catering" : "/order"}
              className="min-w-0 truncate text-base font-black uppercase tracking-tighter sm:text-lg"
            >
              {siteConfig.name}
              {isCatering ? (
                <span className="ml-2 text-[10px] font-medium normal-case tracking-[0.2em] text-muted-foreground">
                  Catering
                </span>
              ) : null}
            </Link>
          </div>

          {/* Right: Reservations + Cart */}
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
            <Link
              href="/reservations"
              className="hidden rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
            >
              Reservations
            </Link>
            {!isCheckoutPage && mounted && (
              <CartSheet>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label={`Cart, ${itemCount} ${itemCount === 1 ? "item" : "items"}`}
                  className="group relative h-11 w-11 rounded-full border-border/60 transition-all duration-300 hover:border-foreground hover:bg-foreground hover:text-background sm:h-10 sm:w-auto sm:gap-2.5 sm:px-5"
                >
                  <ShoppingBag className="h-5 w-5 transition-transform group-hover:scale-110" />
                  <span className="hidden text-xs font-black uppercase tracking-widest sm:inline">
                    Cart
                  </span>
                  {itemCount > 0 && (
                    <span
                      key={itemCount}
                      aria-hidden="true"
                      className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-black text-primary-foreground shadow-lg shadow-primary/20 animate-[cart-pop_300ms_ease-out] sm:relative sm:right-auto sm:top-auto sm:ml-0"
                    >
                      {itemCount}
                    </span>
                  )}
                </Button>
              </CartSheet>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">{children}</main>

      {/* Single persistent bottom order bar — owner.com "View order · $X" (mobile/tablet only) */}
      {mounted && itemCount > 0 && !isCheckoutPage && (
        <div className="fixed inset-x-0 bottom-0 z-40 bg-gradient-to-t from-background via-background/95 to-transparent px-4 pb-[var(--sticky-bar-pb)] pt-5 lg:hidden">
          <CartSheet>
            <Button
              size="lg"
              aria-label={`View order, ${formatPrice(total)}`}
              className="mx-auto flex h-14 w-full max-w-md items-center justify-between rounded-2xl bg-[var(--roma-terracotta)] px-4 text-white shadow-[0_14px_42px_-22px_color-mix(in_srgb,var(--roma-terracotta)_85%,transparent)] transition-all hover:bg-[var(--roma-saffron-deep)] active:scale-[0.98]"
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15">
                  <ShoppingBag className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="text-base font-black tracking-tight">View order</span>
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-[11px] font-black tabular-nums">
                    {itemCount}
                  </span>
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="text-xl font-black tabular-nums tracking-tighter">
                  {formatPrice(total)}
                </span>
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </span>
            </Button>
          </CartSheet>
        </div>
      )}
    </div>
  );
}
